import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Editor, { Monaco } from '@monaco-editor/react';
import { DATA_SOURCE_NAME_COLUMN } from '@squaredup/data-streams';
import Color from 'color';
import { sortBy, uniq } from 'lodash';
import { languages } from 'monaco-editor';
import {
    CompletionService,
    EntityContextType,
    ICompletionItem,
    LanguageIdEnum,
    setupLanguageFeatures,
    vsPlusTheme
} from 'monaco-sql-languages';
import { useEffect, useState } from 'react';
import './languageSetup';

export interface PostgresSQLEditorProps {
    content: string;
    readOnly?: boolean;
    theme?: object;
    datasetDefinitions: {
        tableName: string;
        columns: { name: string; displayName: string }[];
    }[];
    handleError?: (hasError: boolean) => void;
    contentValidator?: (content?: string) => string | boolean;
    onValidUpdatedContent: (content?: string) => void;
}

export const PostgresSQLEditor: React.FC<PostgresSQLEditorProps> = (props) => {
    const editorBackground = Color(
        getComputedStyle(document.body).getPropertyValue('--componentBackgroundSecondary')
    ).hex();
    const { content, contentValidator, onValidUpdatedContent, theme, handleError, readOnly = false } = props;

    const [configError, setConfigError] = useState(false); // Errors in our config
    const [SQLError, setSQLError] = useState(''); // JavaScript language errors

    const { datasetDefinitions } = props;

    const tables = datasetDefinitions.map((table) => table.tableName);
    const columns = sortBy(
        uniq(datasetDefinitions.flatMap((table) => table.columns).filter((c) => c.name !== DATA_SOURCE_NAME_COLUMN))
    );

    const completionService: CompletionService = function (model, position, completionContext, suggestions, entities) {
        return new Promise((resolve) => {
            if (!suggestions) {
                return resolve([
                    {
                        label: 'SELECT',
                        kind: languages.CompletionItemKind.Keyword,
                        detail: 'keyword',
                        sortText: '2SELECT'
                    }
                ]);
            }
            const { keywords, syntax } = suggestions;
            const keywordsCompletionItems: ICompletionItem[] = keywords.map((kw) => ({
                label: kw,
                kind: languages.CompletionItemKind.Keyword,
                detail: 'keyword',
                sortText: '2' + kw
            }));

            let syntaxCompletionItems: ICompletionItem[] = [];

            syntax.forEach((item) => {
                if (item.syntaxContextType === EntityContextType.TABLE) {
                    const tableCompletions: ICompletionItem[] = [
                        ...tables.map((tableName) => ({
                            label: tableName,
                            kind: languages.CompletionItemKind.Variable,
                            detail: 'table',
                            sortText: '1' + tableName
                        }))
                    ];
                    syntaxCompletionItems = [...syntaxCompletionItems, ...tableCompletions];
                }
                if (item.syntaxContextType === EntityContextType.COLUMN) {
                    const columnCompletions: ICompletionItem[] = [
                        ...columns.map(({ name: columnName, displayName }) => ({
                            label: displayName,
                            insertText: /\.|-|\s|\[|\]|\(|\)/gu.test(columnName) ? `"${columnName}"` : columnName,
                            kind: languages.CompletionItemKind.Variable,
                            detail: columnName,
                            sortText: '1' + columnName
                        }))
                    ];
                    syntaxCompletionItems = [...syntaxCompletionItems, ...columnCompletions];
                }
            });

            resolve([...syntaxCompletionItems, ...keywordsCompletionItems]);
        });
    };

    const handleEditorWillMount = (monaco: Monaco) => {
        monaco.editor.defineTheme('squpTheme', {
            base: document.documentElement.dataset.theme === 'dark' ? 'vs-dark' : 'vs',
            inherit: true,
            rules: [
                ...(document.documentElement.dataset.theme === 'dark'
                    ? vsPlusTheme.darkThemeData.rules
                    : vsPlusTheme.lightThemeData.rules),
                { background: editorBackground } as { background: string; token: string }
            ],
            colors: {
                'editor.background': editorBackground,
                'scrollbar.shadow': '#ffffff00'
            },
            ...(theme || {})
        });

        setupLanguageFeatures(LanguageIdEnum.PG, {
            completionItems: {
                enable: true,
                triggerCharacters: [' ', '.'],
                completionService: completionService
            }
        });
    };

    // Watch config/js errors and report up if there is a change
    useEffect(() => {
        handleError && handleError(Boolean(configError) || Boolean(SQLError));
    }, [configError, SQLError, handleError]);

    const handleContentChange = (changedContent: string | undefined) => {
        if (SQLError) {
            setConfigError(false);
        }

        try {
            const updatedContent = changedContent;

            // Allow the editor consumer chance to validate the content:
            const contentErrors = contentValidator && contentValidator(updatedContent);
            if (contentErrors) {
                // @ts-ignore
                setConfigError(contentErrors);
                return;
            }

            setConfigError(false);
            onValidUpdatedContent?.(updatedContent);
            // eslint-disable-next-line no-empty
        } catch {
            // Ignore
        }
    };

    const handleEditorValidation = (markers: { message: string; endLineNumber: number }[]) => {
        const error = markers.length > 0 ? `${markers[0].message} (Line ${markers[0].endLineNumber})` : '';
        setSQLError(error);
    };

    return (
        <div className='flex flex-col flex-grow w-full h-full' data-testid='SQLEditor'>
            <Editor
                language={LanguageIdEnum.PG}
                theme='squpTheme'
                wrapperProps={{
                    className: 'bg-componentBackgroundSecondary border border-outlinePrimary flex-1'
                }}
                loading={<span>Loading editor...</span>}
                defaultValue={content}
                onChange={handleContentChange}
                onValidate={handleEditorValidation}
                beforeMount={handleEditorWillMount}
                options={{
                    scrollBeyondLastLine: false,
                    minimap: { enabled: false },
                    renderLineHighlight: 'none',
                    padding: { top: 10 },
                    automaticLayout: true,
                    readOnly: readOnly,
                    stickyScroll: { enabled: false }
                }}
            />
            {(configError || SQLError) && (
                <div className='flex pt-2 truncate flex-nowrap'>
                    <div>
                        <FontAwesomeIcon icon={faCircleExclamation} className='ml-2 mr-2 text-statusErrorPrimary' />
                    </div>
                    <div>
                        <span className='text-sm text-textSecondary'>{`Configuration error: ${
                            configError || SQLError
                        }`}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
